let _ = t => t,
  _t;
import Raven from 'raven-js';
import { useCallback, useState, useEffect, useRef } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import styledWithTheme from 'foundations-theming/utils';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { filterProps } from 'foundations-components/utils/filterProps';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import styled from 'academy-common-lib/utils/Styling';
import ACThumbnailOverlay from 'academy-common-lib/components/card/ACThumbnailOverlay';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { useStartProgressMutation } from 'academy-common-lib/features/userProgress/userProgressWriterHooks';
import { HS_VIDEO_SURFACE, trackHsVideoComponentRender, trackHsVideoEngaged, trackHsVideoFailedToRender, trackHsVideoFinished, trackHsVideoMilestone, trackHsVideoPlayed, trackHsVideoRenderedSuccessfully } from 'academy-common-lib/utils/hsVideoMetrics';
import VideoProgressOverlay from './VideoProgressOverlay';
import { ClipVideoOverlay } from './ClipVideoOverlay';
import { VideoWatermarkCTA } from './VideoWatermarkCTA';
import { useLesson, useShorts } from '../../../hooks/contentHooks';
import { useHasLearnByDoing, useLearnByDoingLink } from 'academy-common-lib/features/shortContent/shortContentHooks';
import { Metrics } from '../../../Metrics';
import { usageTracker } from '../../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VideoWrapper = styledWithTheme.section(_t || (_t = _`
  position: relative;
  ${0}
`), props => props.$cssStyles || {});
const PausedThumbnailOverlayHeaderContainer = styled.div.withConfig({
  displayName: "Video__PausedThumbnailOverlayHeaderContainer"
})(["border-bottom-right-radius:5px;border-top-right-radius:5px;background:rgba(0,0,0,0.5);padding:8px;margin-left:-12px;pointer-events:auto;"]);
const noop = () => {};
export const Video = ({
  autoPlay,
  lessonId,
  contentId,
  shortId,
  language,
  mutedByDefault,
  startTime = 0,
  onPlay = noop,
  onProgress = noop,
  onFinish = noop,
  onEngagement = noop,
  onWatermarkClick = noop,
  isPublic = false,
  openLinksInNewTab,
  renderContext,
  creatorHubId,
  enablePictureInPicture,
  hublet,
  link,
  videoType: videoTypeEnum,
  clipRange,
  videoWrapperProps,
  onTimeUpdate
}) => {
  var _lesson$content, _ref, _content$duration;
  const hasMilestone75 = useRef(false);
  const hasMilestone90 = useRef(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [shouldRenderFinishCTA, setShouldRenderFinishCTA] = useState(false);
  const {
    tokenProps
  } = filterProps(videoWrapperProps || {});
  const videoCssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    lesson
  } = useLesson(lessonId, isPublic);
  const {
    shortFormContent
  } = useShorts(shortId, isPublic);
  const isParentWindowDesktop = (() => {
    try {
      var _window$top;
      return !!((_window$top = window.top) !== null && _window$top !== void 0 && _window$top.matchMedia('(min-width: 1024px)').matches);
    } catch (_unused) {
      return false;
    }
  })();
  const hasLearnByDoing = useHasLearnByDoing({
    shortId: shortId,
    learnByDoingEnabled: shortFormContent === null || shortFormContent === void 0 ? void 0 : shortFormContent.learnByDoingEnabled,
    isParentWindowDesktop
  });
  const learnByDoingHref = useLearnByDoingLink({
    shortId: shortId
  });
  const [startShortProgress] = useStartProgressMutation();
  const content = (lesson === null || lesson === void 0 || (_lesson$content = lesson.content) === null || _lesson$content === void 0 ? void 0 : _lesson$content.find(c => c.id === contentId)) || shortFormContent;
  const contentName = content === null || content === void 0 ? void 0 : content.name;
  // @ts-expect-error typescript@5.7.3 stricter checks
  const videoDuration = (_ref = (_content$duration = content === null || content === void 0 ? void 0 : content.duration) !== null && _content$duration !== void 0 ? _content$duration : shortFormContent === null || shortFormContent === void 0 ? void 0 : shortFormContent.duration) !== null && _ref !== void 0 ? _ref : 0;
  const playerType = 'HUBSPOT_VIDEO';
  // @ts-expect-error typescript@5.7.3 stricter checks
  const videoId = content === null || content === void 0 ? void 0 : content.reference;
  const videoType = videoTypeEnum ? readUnhandledEnumCase(videoTypeEnum) : undefined;
  useEffect(() => {
    hasMilestone75.current = false;
    hasMilestone90.current = false;
  }, [videoId, contentId, lessonId, shortId]);
  const handlePlay = useCallback(() => {
    setShowOverlay(false);
    setShouldRenderFinishCTA(false);
    if (!isPublic && shortId !== undefined && language !== undefined) {
      startShortProgress({
        variables: {
          contentId: shortId,
          contentType: 'SHORT_FORM_CONTENT',
          language: convertToSupportedLanguage(language)
        }
      }).catch(Raven.captureException.bind(Raven));
    }
    usageTracker.track('interaction', {
      action: 'video-started',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    trackHsVideoPlayed(Metrics, {
      surface: HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI
    });
    onPlay();
    setPaused(false);
    usageTracker.track('videoUsage', {
      type: 'started',
      playerType,
      videoId,
      videoTitle: contentName,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, setShowOverlay, lessonId, onPlay, renderContext, shortId, startTime, videoId, contentName, startShortProgress, language, isPublic]);
  const handleLoaded = useCallback(() => {
    usageTracker.track('interaction', {
      action: 'video-loaded',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    trackHsVideoRenderedSuccessfully(Metrics, HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI);
    usageTracker.track('videoUsage', {
      type: 'loaded',
      playerType,
      videoType,
      videoId,
      videoTitle: contentName,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, renderContext, shortId, startTime, videoId, videoType, contentName]);
  const handleActivation = useCallback(() => {
    onEngagement();
    usageTracker.track('interaction', {
      action: 'video-engagement',
      contentId,
      lessonId,
      isTimestamped: startTime > 0
    });
    trackHsVideoEngaged(Metrics, {
      surface: HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI
    });
    usageTracker.track('videoUsage', {
      type: 'engaged',
      playerType,
      videoType,
      videoId,
      videoTitle: contentName,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, onEngagement, renderContext, startTime, videoId, videoType, shortId, contentName]);
  const handleFinish = useCallback(() => {
    setShouldRenderFinishCTA(true);
    usageTracker.track('interaction', {
      action: 'video-finished',
      contentId,
      lessonId,
      shortId,
      isTimestamped: startTime > 0
    });
    trackHsVideoFinished(Metrics, {
      surface: HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI
    });
    onFinish();
    usageTracker.track('videoUsage', {
      type: 'finished',
      playerType,
      videoType,
      videoId,
      videoTitle: contentName,
      renderContext,
      creatorHubId,
      contentId,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, lessonId, onFinish, renderContext, shortId, startTime, videoId, videoType, contentName]);
  const handleMinutesWatched = useCallback(viewDuration => {
    usageTracker.track('videoUsage', {
      type: 'minutes-watched',
      playerType,
      videoId,
      viewDuration,
      videoTitle: contentName,
      renderContext,
      creatorHubId,
      contentId,
      videoType,
      lessonId,
      shortId,
      sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
    });
  }, [contentId, creatorHubId, renderContext, videoId, videoType, lessonId, shortId, contentName]);
  const handleVideoError = useCallback(e => {
    const detail = e.detail || {};
    const resolvedErrorType = detail.errorType || 'UNKNOWN';
    trackHsVideoFailedToRender(Metrics, {
      errorType: resolvedErrorType,
      surface: HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI
    });
    Raven.captureException(new Error(`HS_VIDEO_FAILED_RENDER_EMBED: ${resolvedErrorType}`), {
      tags: {
        hsVideoErrorType: resolvedErrorType
      },
      fingerprint: ['hs-video-failed-render-embed', resolvedErrorType],
      extra: {
        videoId,
        contentId,
        lessonId,
        shortId,
        errorMessage: detail.payload || detail.message
      }
    });
  }, [contentId, lessonId, shortId, videoId]);
  const handleTimeUpdateWithMilestones = useCallback(seconds => {
    if (onTimeUpdate) onTimeUpdate(seconds);
    const duration = videoDuration;
    if (!duration || duration <= 0) return;
    const ratio = seconds / duration;
    if (!hasMilestone75.current && ratio >= 0.75) {
      hasMilestone75.current = true;
      trackHsVideoMilestone(Metrics, '0.75', HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI);
    }
    if (!hasMilestone90.current && ratio >= 0.9) {
      hasMilestone90.current = true;
      trackHsVideoMilestone(Metrics, '0.9', HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI);
    }
  }, [onTimeUpdate, videoDuration]);
  useEffect(() => {
    if (!videoId) return;
    trackHsVideoComponentRender(Metrics, HS_VIDEO_SURFACE.EMBEDDED_ACADEMY_UI);
  }, [videoId]);
  useEffect(() => {
    if (content) {
      usageTracker.track('videoUsage', {
        type: 'rendered',
        playerType,
        videoId,
        videoTitle: contentName,
        renderContext,
        creatorHubId,
        contentId,
        videoType,
        lessonId,
        shortId,
        sourceUrl: window.location !== window.parent.location ? document.referrer : document.location.href
      });
    }
  }, [content, contentId, creatorHubId, playerType, renderContext, videoId, videoType, lessonId, shortId, contentName]);
  if (!content) return null;
  const isValidClipRange = clipRange && clipRange.startTime < clipRange.endTime && clipRange.endTime < videoDuration;
  return /*#__PURE__*/_jsxs(VideoWrapper, {
    $cssStyles: videoCssStyles,
    onClick: () => setShowOverlay(false),
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    children: [/*#__PURE__*/_jsx(ACVideoPlayer, {
      autoPlay: autoPlay,
      creatorHubId: (content === null || content === void 0 ? void 0 : content.hubId) || undefined,
      creatorRegion: hublet,
      "data-test-id": "embedded-academy-video-player",
      enablePictureInPicture: enablePictureInPicture,
      mutedByDefault: mutedByDefault,
      onActivation: handleActivation,
      onError: handleVideoError,
      onFinish: handleFinish,
      onLoading: () => setShowOverlay(false),
      onPause: () => setPaused(true),
      onPlay: handlePlay,
      onProgress: progress => {
        if (shouldRenderFinishCTA) setShouldRenderFinishCTA(false);
        onProgress(progress);
      },
      onReady: handleLoaded,
      onTimeProgress: handleMinutesWatched,
      onTimeUpdate: handleTimeUpdateWithMilestones,
      startTime: !showOverlay ? startTime : undefined,
      videoId: videoId
    }), renderContext !== 'embedded-learnByDoing' && /*#__PURE__*/_jsxs(_Fragment, {
      children: [!showOverlay && !shouldRenderFinishCTA && (paused || hovered) && /*#__PURE__*/_jsx(ACThumbnailOverlay, {
        header: /*#__PURE__*/_jsx(PausedThumbnailOverlayHeaderContainer, {
          children: /*#__PURE__*/_jsx(VideoWatermarkCTA, {
            contentId: contentId,
            hasLearnByDoing: hasLearnByDoing,
            learnByDoingHref: learnByDoingHref,
            lessonId: lessonId,
            link: link,
            onClick: onWatermarkClick,
            openLinksInNewTab: openLinksInNewTab,
            renderContext: renderContext,
            shortId: shortId,
            trackAction: hasLearnByDoing ? 'watermark-learn-by-doing-click' : 'watermark-link-to-academy-click'
          })
        })
      }), shouldRenderFinishCTA && /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        blockSize: "100%",
        inlineSize: "100%",
        insetBlockStart: "0",
        insetInlineStart: "0",
        justifyContent: "center",
        pointerEvents: "none",
        position: "absolute",
        children: /*#__PURE__*/_jsx(View, {
          backgroundColor: "fill-primary-default",
          borderRadius: "full",
          paddingBlock: "200",
          paddingInline: "400",
          pointerEvents: "auto",
          children: /*#__PURE__*/_jsx(VideoWatermarkCTA, {
            contentId: contentId,
            hasLearnByDoing: hasLearnByDoing,
            learnByDoingHref: learnByDoingHref,
            lessonId: lessonId,
            link: link,
            onClick: onWatermarkClick,
            openLinksInNewTab: openLinksInNewTab,
            renderContext: renderContext,
            shortId: shortId,
            trackAction: hasLearnByDoing ? 'end-of-video-learn-by-doing-click' : 'end-of-video-watch-on-academy-click'
          })
        })
      }), showOverlay && (isValidClipRange ? /*#__PURE__*/_jsx(ClipVideoOverlay, {
        clipRange: clipRange,
        videoLength: videoDuration
      }) : /*#__PURE__*/_jsx(VideoProgressOverlay, {
        contentId: contentId,
        hasLearnByDoing: hasLearnByDoing,
        isPublic: isPublic,
        learnByDoingHref: learnByDoingHref,
        lessonId: lessonId,
        link: link,
        onWatermarkClick: onWatermarkClick,
        openLinksInNewTab: openLinksInNewTab,
        renderContext: renderContext,
        shortId: shortId
      }))]
    })]
  });
};